import {
  ColorKey,
  relevanceJudgementsMapping,
  TextDecoration,
} from '../../../models/relevance';
import { ExpandComponent } from './ExpandComponent';
import { formatCitation } from '../../../utils/text';
import { RelevanceCriterionJudgement } from '../../../models/result-set';
import { RelevanceIcon } from './RelevanceIcon';

import styled from 'styled-components';

interface SummaryProps {
  relevanceJudgement?: number;
  relevanceScore?: number;
  text?: string;
  relevanceCriteriaJudgements: Array<RelevanceCriterionJudgement>;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isLegalToShow: boolean;
}

export function Summary(props: SummaryProps): JSX.Element {
  const {
    relevanceJudgement,
    relevanceScore,
    text,
    relevanceCriteriaJudgements,
    isExpanded,
    setIsExpanded,
    isLegalToShow,
  } = props;
  return (
    <MainLayout>
      <span>
        <RelevanceIcon
          relevanceJudgement={relevanceJudgement}
          relevanceScore={relevanceScore}
        />
        {text && <span>{text}</span>}
      </span>
      <FooterContentDiv>
        <CriteriaListDiv
          className={isExpanded && isLegalToShow ? 'expanded' : undefined}
        >
          {relevanceCriteriaJudgements.map((criteria) => {
            const mappedRelevance = relevanceJudgementsMapping.find(
              (i) => i.relevance === criteria.relevance,
            );
            return (
              mappedRelevance && (
                <div key={criteria.name}>
                  <CriteriaDiv
                    colorkey={mappedRelevance.colorKey}
                    textDecoration={mappedRelevance.textDecoration}
                  >
                    <span>{criteria.name}</span>
                  </CriteriaDiv>
                  {isExpanded && (
                    <SnippetSpan>
                      {formatCitation(criteria.relevantSnippets)}
                    </SnippetSpan>
                  )}
                </div>
              )
            );
          })}
        </CriteriaListDiv>
        {!isExpanded && (
          <ExpandComponent
            isExpanded={isExpanded}
            setExpanded={setIsExpanded}
          />
        )}
      </FooterContentDiv>
    </MainLayout>
  );
}

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  font-size: 14px;
  gap: 8px;
`;

const FooterContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  gap: 8px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CriteriaDiv = styled.div<{
  colorkey: ColorKey;
  textDecoration: TextDecoration;
}>`
  display: flex;
  color: ${({ colorkey, theme }) => `${theme.other.relevance[colorkey]}`};
  background-color: ${({ theme }) => theme.color.background.paper.secondary};
  font-family: ${({ theme }) => theme.font.family.primary};
  text-decoration: ${({ textDecoration }) => textDecoration};
  font-weight: 400;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.border.radius.medium};
  width: fit-content;
  white-space: nowrap;
  user-select: none;
`;

const CriteriaListDiv = styled.div`
  display: flex;
  flex-wrap: wrap;

  flex-direction: row;
  margin: 0px;
  gap: 8px;

  &.expanded {
    flex-direction: column;
    margin: 5px 0px;
    gap: 12px;
  }
`;

const SnippetSpan = styled.span`
  margin-bottom: 8px;
  font-style: italic;
`;
